/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable max-len */
import { CircularProgress, makeStyles, Theme, Tooltip } from '@material-ui/core';
import { GenericPanelComp, useModePanelStyle } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import React, { CSSProperties, useMemo } from 'react';
import { EntityExclusionCustomSettings, EntityUIComponentCollection, filterEntityClassesByEntityClassesExclusionCustomSettings,
    filterEntityInstancesByEntityInstancesExclusionCustomSettings, isEntityExclusionCustomSettings } from '..';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectAllEntityChildren, useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingStringValue } from '../../../utils/utils';
import { EntityUIComponentWithChildrenProps } from '../EntityDetailsLayouts';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        panelHeader: {
            padding: theme.spacing(1),
        },

        panelContent: {
            overflow      : 'auto',
            padding       : 0,
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'center',
            width         : '100%',
        },

        mapImageContainer: {
            position: 'relative',
            width   : '100%',
            overflow: 'hidden',
        },

        mapImage: {
            display : 'block',
            position: 'relative',
            width   : '100%',
        },

        childMarker: {
            position    : 'absolute',
            width       : 50,
            height      : 50,
            borderRadius: '50%',
            display     : 'block',
            background  : theme.palette.background.paper,
            boxShadow   : theme.shadows[2],
            overflow    : 'hidden',
            padding     : theme.spacing(0.5),
            top         : '0%',
            left        : '0%',
            transform   : 'translate3d(-50%, -50%, 0)',
            cursor      : 'pointer',
        },

        childMarkerImage: {
            width       : '100%',
            height      : '100%',
            display     : 'block',
            borderRadius: '50%',
            objectFit   : 'cover',
        },
    };
}, { name: 'EntityStaticChildrenMapUIComponentCustomSettings', index: 1 });


// The default name of the position attributes that the component will use to look for the child entity's positions
export const DEFAULT_ENTITY_STATIC_POSITION_ATTRIBUTE_NAME_TOP = 'pos_top';
export const DEFAULT_ENTITY_STATIC_POSITION_ATTRIBUTE_NAME_LEFT = 'pos_left';



/**
 * Interface for EntityStaticChildrenMapUIComponentCustomSettings's customSettings. These are the props/value that can be
 * configured for the 'EntityStaticChildrenMapUIComponentCustomSettings' from the entityClass's uiSettings.
 */
export interface EntityStaticChildrenMapUIComponentCustomSettings extends EntityExclusionCustomSettings {

    // The title to display in the header. This can be a translation key but the admin will need to add the translation for this key
    // in the project's translationOverride settings
    readonly titleKey?: string | undefined;
    readonly icon?: string | undefined;

    // The entity attribute to use to get the parent entity's image, "image" or "icon", or the name of a custom attribute where the image is stored.
    // By default, the component will use the entity's "image" attr this is not provided. Here are some examples:
    // mapImageAttr = "image"                                   // To use the parent's image as the map image
    // mapImageAttr = "icon"                                    // To use the parent's icon as the map image
    // mapImageAttr = "floor_plan_image"                        // To use the parent's custom attribute called 'floor_plan_image'
    readonly mapImageAttr?: 'image' | 'icon' | string | undefined;

    // The child entity attribute to use to get the child's image, "image" or "icon". By default, the component will use the child entity's "image" attr
    readonly childImageAttr?: 'image' | 'icon' | undefined;

    // The URL of the image to use when a child entity does not have image. This is the GLOBAL image that apply to all child. If we want to customize
    // a different image for a specific entity class then we can use the defaultChildImageUrlPerEntityClass settings.
    readonly defaultChildImageUrl?: string | undefined;

    // The URL of the image to use PER entity class when a child entity does not have image
    readonly defaultChildImageUrlPerEntityClass?: {
        readonly [entityClass: string]: string | undefined;
    } | undefined;

    // The name of the entity attribute where the child's left/top positions are stored. By default, the component will look in the entity's
    // "pos_left" and "pos_top" attributes. If a project use different attribute names then the admin can provide the attributes names that the
    // project uses.
    readonly childPositionAttributeNames?: {
        // The name of the attribute which has the TOP position value. The value must be in % relative to the map image's height
        readonly top?: string | undefined;

        // The name of the attribute which has the LEFT position value. The value must be  % relative to the map image's width
        readonly left?: string | undefined;
    } | undefined;

    // The custom CSS style for the outer container element that wrap the map image
    readonly mapImageContainerStyle?: CSSProperties | undefined;

    // The custom CSS style for the map image element
    readonly mapImageStyle?: CSSProperties | undefined;

    // The custom CSS style for the child marker container element
    readonly childMarkerStyle?: CSSProperties | undefined;

    // The custom CSS style for the child marker image element
    readonly childMarkerImageStyle?: CSSProperties | undefined;
}



/**
 * type guard function to check if an object is a type of EntityStaticChildrenMapUIComponentCustomSettings
 */
const isEntityStaticChildrenMapUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityStaticChildrenMapUIComponentCustomSettings => {

    const supportedImageAttributes = ['image', 'icon'];
    const settings = obj as EntityStaticChildrenMapUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey)) {
        return errorLogger?.logError({
            message: '"titleKey" must be undefined or a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.icon)) {
        return errorLogger?.logError({
            message: '"icon" must be undefined or a non-empty string.',
            value  : settings.icon,
            path   : [...configPath, 'icon'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.mapImageAttr)) {
        return errorLogger?.logError({
            message: '"mapImageAttr" must be undefined a non-empty string.',
            value  : settings.mapImageAttr,
            path   : [...configPath, 'mapImageAttr'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultChildImageUrl)) {
        return errorLogger?.logError({
            message: '"defaultChildImageUrl" must be undefined a non-empty string.',
            value  : settings.defaultChildImageUrl,
            path   : [...configPath, 'defaultChildImageUrl'],
        }) ?? false;
    }

    if (settings.defaultChildImageUrlPerEntityClass !== undefined) {
        if (!isObjectLiteral(settings.defaultChildImageUrlPerEntityClass)) {
            return errorLogger?.logError({
                message: '"defaultChildImageUrlPerEntityClass" must undefined or be an object.',
                value  : settings.defaultChildImageUrlPerEntityClass,
                path   : [...configPath, 'defaultChildImageUrlPerEntityClass'],
            }) ?? false;
        }

        // All values in defaultChildImageUrlPerEntityClass must be strings
        if (!Object.entries(settings.defaultChildImageUrlPerEntityClass).every(([entityClass, imageUrl]) => {
            if (!isValidSettingStringValue(imageUrl, true)) {
                return errorLogger?.logError({
                    message: 'All values in "defaultChildImageUrlPerEntityClass" must be non-empty strings.',
                    value  : imageUrl,
                    path   : [...configPath, 'defaultChildImageUrlPerEntityClass', `${entityClass}`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    if (!isValidSettingStringValue(settings.childImageAttr, false, false, supportedImageAttributes)) {
        return errorLogger?.logError({
            message: `"childImageAttr" must be undefined or one of the following [${
                supportedImageAttributes.map((value) => {
                    return `"${value}"`;
                }).join(', ')}].`,
            value: settings.childImageAttr,
            path : [...configPath, 'childImageAttr'],
        }) ?? false;
    }

    // "childPositionAttributes" is optional but if it is provided, it must have the correct values.
    if (settings.childPositionAttributeNames !== undefined) {
        if (!isObjectLiteral(settings.childPositionAttributeNames)) {
            return errorLogger?.logError({
                message: '"childPositionAttributeNames" must be an object.',
                value  : settings.childPositionAttributeNames,
                path   : [...configPath, 'childPositionAttributeNames'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.childPositionAttributeNames.top)) {
            return errorLogger?.logError({
                message: '"top" must be undefined or a non-empty string',
                value  : settings.childPositionAttributeNames.top,
                path   : [...configPath, 'childPositionAttributeNames', 'top'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.childPositionAttributeNames.left)) {
            return errorLogger?.logError({
                message: '"left" must be undefined or a non-empty string',
                value  : settings.childPositionAttributeNames.left,
                path   : [...configPath, 'childPositionAttributeNames', 'left'],
            }) ?? false;
        }
    }

    // "mapImageContainerStyle", "mapImageStyle", "childMarkerStyle" are optional but if they are provided, they must be objects
    if (settings.mapImageContainerStyle !== undefined && !isObjectLiteral(settings.mapImageContainerStyle)) {
        return errorLogger?.logError({
            message: '"mapImageContainerStyle" must be undefined or a CSSProperties object.',
            value  : settings.mapImageContainerStyle,
            path   : [...configPath, 'mapImageContainerStyle'],
        }) ?? false;
    }

    if (settings.mapImageStyle !== undefined && !isObjectLiteral(settings.mapImageStyle)) {
        return errorLogger?.logError({
            message: '"mapImageStyle" must be undefined or a CSSProperties object.',
            value  : settings.mapImageStyle,
            path   : [...configPath, 'mapImageStyle'],
        }) ?? false;
    }

    if (settings.childMarkerStyle !== undefined && !isObjectLiteral(settings.childMarkerStyle)) {
        return errorLogger?.logError({
            message: '"childMarkerStyle" must be undefined or a CSSProperties object.',
            value  : settings.childMarkerStyle,
            path   : [...configPath, 'childMarkerStyle'],
        }) ?? false;
    }

    if (settings.childMarkerImageStyle !== undefined && !isObjectLiteral(settings.childMarkerImageStyle)) {
        return errorLogger?.logError({
            message: '"childMarkerImageStyle" must be undefined or a CSSProperties object.',
            value  : settings.childMarkerImageStyle,
            path   : [...configPath, 'childMarkerImageStyle'],
        }) ?? false;
    }

    if (!isEntityExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};



export interface EntityStaticChildrenMapUIComponentProps extends EntityUIComponentWithChildrenProps {
    readonly className?: string | undefined;
    readonly customSettings?: EntityStaticChildrenMapUIComponentCustomSettings | undefined;
}


/**
 * This is an EntityUIComponent that display it's children in a static 2D map.
 */
export const EntityStaticChildrenMapUIComponent: React.FC<EntityStaticChildrenMapUIComponentProps> = (
    props: EntityStaticChildrenMapUIComponentProps,
) => {

    const project = useSelectProject(props.projectId);

    // Get the entity object for the current entity
    const selectedEntity = useSelectEntity(props.projectId, props.entityId);
    const classes = useStyle();
    const panelClasses = useModePanelStyle();

    const { trans } = useRitzTranslation(project);

    // Get all children entities
    const childEntities = useSelectAllEntityChildren(
        props.projectId,
        props.entityId,
    );

    // Filter out the entities that are NOT instances of the entity classes specified in includeEntityClasses
    const filteredChildEntities = useMemo(() => {
        if (childEntities) {
            const childrenFilteredByEntityClasses = filterEntityClassesByEntityClassesExclusionCustomSettings(childEntities, props.customSettings);
            return filterEntityInstancesByEntityInstancesExclusionCustomSettings(childrenFilteredByEntityClasses, props.customSettings);
        }

        return undefined;
    }, [childEntities, props.customSettings]);


    // Get the parent image URL. If "mapImageAttr" is set to "icon" or "image" then use the entity's icon/image. Else if it is set to some
    // other string, assume that string is a URL and use that url. If this is not set, use the entity's image
    const mapImage = useMemo(() => {
        if (selectedEntity) {
            const mapImageAttr = props.customSettings?.mapImageAttr;
            if (mapImageAttr === 'icon') {
                return selectedEntity.displaySettings.iconData?.url;
            }
            if (mapImageAttr === 'image') {
                return selectedEntity.displaySettings.imageData?.url;
            }

            // Check the entity's attributes to see if there is an attribute with this name. If there is, use its value as the image URL
            if (mapImageAttr && typeof selectedEntity.attributes?.[mapImageAttr] === 'string') {
                return selectedEntity.attributes[mapImageAttr] as string;
            }

            return selectedEntity.displaySettings.imageData?.url;
        }

        return undefined;
    }, [props.customSettings?.mapImageAttr, selectedEntity]);


    return (
        <>
            {selectedEntity && filteredChildEntities && (
                <GenericPanelComp
                    className={clsx(props.className, classes.root)}
                    style={props.style}
                    title={props.customSettings?.titleKey ? trans(props.customSettings.titleKey) : ''}
                    icon={props.customSettings?.icon}
                    headerless={!props.customSettings?.titleKey && !props.customSettings?.icon}
                >
                    <div className={clsx(panelClasses.panelContent, classes.panelContent)}>
                        <div
                            className={clsx(classes.mapImageContainer)}
                            style={props.customSettings?.mapImageContainerStyle}
                        >
                            {/* The background image of the map */}
                            <img
                                className={clsx(classes.mapImage)}
                                style={props.customSettings?.mapImageStyle}
                                src={mapImage}
                            />
                            {filteredChildEntities.map((child) => {
                                const attributes = child.attributes;

                                // Get the child's top/left position from the childEntity's position attributes. The position attributes are
                                // defined in customSettings.childPositionAttributeNames. If they are not defined then we will use the default
                                // attributes. And if the entity doesn't have any value for the position attributes, the entity will be shown
                                // in the middle of the map
                                const topAttributeName = props.customSettings?.childPositionAttributeNames?.top
                                                            ?? DEFAULT_ENTITY_STATIC_POSITION_ATTRIBUTE_NAME_TOP;
                                const leftAttributeName = props.customSettings?.childPositionAttributeNames?.left
                                                            ?? DEFAULT_ENTITY_STATIC_POSITION_ATTRIBUTE_NAME_LEFT;

                                const topPosValue = (attributes?.[topAttributeName] ?? '50%').toString();
                                const leftPosValue = (attributes?.[leftAttributeName] ?? '50%').toString();


                                // Get the child's image URL. If "childImageAttr" is set to "icon" then use the entity's icon image. Else use
                                // the entity's image's image
                                const imageUrl = (props.customSettings?.childImageAttr === 'icon'
                                    ? child.displaySettings.iconData?.url : child.displaySettings.imageData?.url)
                                    ?? props.customSettings?.defaultChildImageUrlPerEntityClass?.[child.entityClass]    // try to use the defaultImage per entity class
                                    ?? props.customSettings?.defaultChildImageUrl;                                      // use the global default image


                                return (
                                    <Tooltip
                                        key={child.entityId}
                                        title={child.displaySettings.name}
                                    >
                                        <div
                                            className={classes.childMarker}
                                            style={{
                                                ...props.customSettings?.childMarkerStyle,
                                                top : topPosValue,
                                                left: leftPosValue,
                                            }}
                                            onClick={(event) => {
                                                if (event) {
                                                    event.stopPropagation();
                                                    event.preventDefault();
                                                }
                                                if (props.onShowEntityDetails) {
                                                    props.onShowEntityDetails(child);
                                                }
                                            }}
                                        >
                                            <img
                                                className={clsx(classes.childMarkerImage)}
                                                style={props.customSettings?.childMarkerImageStyle}
                                                src={imageUrl}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>
                </GenericPanelComp>
            )}

            {!childEntities && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityStaticChildrenMapUIComponentCustomSettings', EntityStaticChildrenMapUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Static_Children_Map_Comp',
    {
        componentFunc          : EntityStaticChildrenMapUIComponent,
        customSettingsValidator: isEntityStaticChildrenMapUIComponentCustomSettings,
    },
);
